import { zipSync, unzip } from 'fflate'

export interface ZipFileEntry {
  name: string
  size: number
  /** Plain ArrayBuffer-backed view, safe to use as a BlobPart. */
  data: Uint8Array<ArrayBuffer>
}

/**
 * Build a ZIP archive from local files.
 * Progress reflects per-file compression; the final sync pass is fast because
 * entries are already in memory. Note: zipSync blocks the main thread briefly
 * — acceptable for typical ZIP payloads, unlike video (which uses a Worker).
 */
export async function createZip(
  files: File[],
  onProgress: (progress: number) => void,
): Promise<Blob> {
  const entries: Record<string, Uint8Array> = {}

  for (let i = 0; i < files.length; i += 1) {
    const file = files[i]
    if (!file) continue

    const buffer = new Uint8Array(await file.arrayBuffer())
    entries[file.name] = buffer
    onProgress(Math.round(((i + 1) / files.length) * 70))
    // Yield to the event loop so the UI can repaint between reads.
    await new Promise((resolve) => setTimeout(resolve, 0))
  }

  onProgress(90)
  const zipped = zipSync(entries, { level: 9 })
  onProgress(100)
  return new Blob([zipped], { type: 'application/zip' })
}

/**
 * Extract a ZIP archive, returning entries with their decompressed bytes.
 * Uses async unzip so the UI stays responsive. Note: fflate's async unzip
 * exposes no per-chunk progress callback, so progress reflects the read
 * phase (0-60%) then completes after decompression.
 */
export function extractZip(
  file: File,
  onProgress: (progress: number) => void,
): Promise<ZipFileEntry[]> {
  return new Promise<ZipFileEntry[]>((resolve, reject) => {
    file
      .arrayBuffer()
      .then((buffer) => {
        onProgress(60)

        unzip(new Uint8Array(buffer), (err, files) => {
          if (err) {
            reject(err)
            return
          }
          onProgress(100)
          resolve(
            Object.entries(files).map(([name, data]) => ({
              name,
              size: data.byteLength,
              // Copy into a plain ArrayBuffer-backed view so the value is a
              // valid BlobPart regardless of the source buffer type.
              data: new Uint8Array(data),
            })),
          )
        })
      })
      .catch(reject)
  })
}
